/* Nukemizer Measurements tab: front and side drawings of the build you have selected, rendered from the 3D model,
   with dimension lines and arrows placed from the real geometry and labelled with the published measurements. */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const box = document.querySelector('[data-nkz-meas]');
const root = box && box.closest('.stg');
const cfgEl = document.querySelector('[data-nb-config]');
if (box && root && cfgEl) start();

function start() {
  const panel = box.closest('[role="tabpanel"]');
  let R = null, S = null, sig = '', busy = false;
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const frames = () => { const o = {}; try { (JSON.parse(cfgEl.textContent).frames || []).forEach((f) => { o[f.key] = f; }); } catch (e) {} return o; };

  function renderer() {
    if (R) return R;
    R = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    R.setPixelRatio(1); R.outputColorSpace = THREE.SRGBColorSpace; R.toneMapping = THREE.ACESFilmicToneMapping; R.toneMappingExposure = 1.1; R.setClearColor(0, 0);
    S = new THREE.Scene(); S.environment = new THREE.PMREMGenerator(R).fromScene(new RoomEnvironment(R), 0.04).texture;
    const k = new THREE.DirectionalLight(0xffffff, 1.4); k.position.set(-120, 160, 220); S.add(k);
    const f = new THREE.DirectionalLight(0xffffff, 0.7); f.position.set(200, 60, 80); S.add(f);
    S.add(new THREE.HemisphereLight(0xffffff, 0xcfcfcf, 0.7));
    return R;
  }
  const wbox = (o) => new THREE.Box3().setFromObject(o);
  function meshes(g) { const m = { frame: null, lenses: [], arms: [] }; g.traverse((o) => { if (!o.isMesh || !o.visible) return; const n = o.name;
    if (n === 'frame') m.frame = o; else if (n.startsWith('lens')) m.lenses.push(o); else if (n.startsWith('arm')) m.arms.push(o); }); return m; }

  /* one orthographic view -> {img, w, h, P(v3)->[x,y]} */
  function shot(g, view, W) {
    const r = renderer(), b = wbox(g), c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    const across = view === 'front' ? s.x : s.z, tall = s.y, pad = 0.18, aspect = (across * (1 + pad * 2)) / (tall * (1 + pad * 2) + across * (view === 'front' ? 0.46 : 0.3));
    const H = Math.round(W / aspect), half = across * (0.5 + pad), vh = half / aspect;
    const cam = new THREE.OrthographicCamera(-half, half, vh, -vh, 1, 5000);
    if (view === 'front') cam.position.set(c.x, c.y, c.z + 1000); else cam.position.set(c.x + 1000, c.y, c.z);
    cam.lookAt(c); cam.updateMatrixWorld(); cam.updateProjectionMatrix();
    r.setSize(W, H, false); r.render(S, cam);
    const P = (v) => { const p = v.clone().project(cam); return [(p.x + 1) / 2 * W, (1 - p.y) / 2 * H]; };
    return { img: r.domElement.toDataURL('image/png'), w: W, h: H, P };
  }

  const ARR = '<defs><marker id="nkm-a" viewBox="0 0 12 12" refX="6" refY="6" markerWidth="12" markerHeight="12" markerUnits="userSpaceOnUse"><circle cx="6" cy="6" r="4.6" fill="#FF651F" stroke="#fff" stroke-width="1.6"/></marker></defs>';
  function dim(x1, y1, x2, y2, label, side) {   // dimension line with arrows at both ends and a label tag
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2, vert = Math.abs(x2 - x1) < 1;
    const tx = vert ? mx + (side || 1) * 20 : mx, ty = vert ? my : my + (side || -1) * 20;
    const rot = vert ? ' transform="rotate(' + (side > 0 ? 90 : -90) + ' ' + tx.toFixed(1) + ' ' + ty.toFixed(1) + ')"' : '';
    return '<line x1="' + x1.toFixed(1) + '" y1="' + y1.toFixed(1) + '" x2="' + x2.toFixed(1) + '" y2="' + y2.toFixed(1) + '" class="nkm-l" marker-start="url(#nkm-a)" marker-end="url(#nkm-a)"/>' +
      '<text x="' + tx.toFixed(1) + '" y="' + ty.toFixed(1) + '" text-anchor="middle" dominant-baseline="middle" class="nkm-t"' + rot + '>' + esc(label) + '</text>';
  }
  const ext = (x1, y1, x2, y2) => '<line x1="' + x1.toFixed(1) + '" y1="' + y1.toFixed(1) + '" x2="' + x2.toFixed(1) + '" y2="' + y2.toFixed(1) + '" class="nkm-e"/>';
  const V = (x, y, z) => new THREE.Vector3(x, y, z);
  const val = (m, k) => (m && m[k]) ? m[k] : null;

  function draw(nb, f) {
    const De = nb.cur().frame.scene.parent, g = De.clone(true); S || renderer(); S.add(g); g.updateMatrixWorld(true);
    const M = meshes(g), meas = f.meas || {}, out = [];
    /* FRONT */
    const fr = M.frame ? wbox(M.frame) : wbox(g), z = fr.max.z;
    const sF = shot(g, 'front', 880); let svg = '';
    const [lx, ty] = sF.P(V(fr.min.x, fr.max.y, z)), [rx, by] = sF.P(V(fr.max.x, fr.min.y, z));
    const G = 7, R1 = by + 34, R2 = by + 82, xr = rx + 30;   // gap from the frame, first and second rows below, column to the right
    const fw = val(meas, 'Frame width');
    if (fw) { const y = ty - 30; svg += ext(lx, ty - G, lx, y) + ext(rx, ty - G, rx, y) + dim(lx, y, rx, y, 'Frame width ' + fw, -1); }
    const L = M.lenses.map((m) => wbox(m)).sort((a, b) => a.min.x - b.min.x);
    const lh = val(meas, 'Lens height');
    if (L.length >= 2) {
      const a = L[0], b = L[L.length - 1];
      const [ax1] = sF.P(V(a.min.x, 0, z)), [ax2] = sF.P(V(a.max.x, 0, z)), [bx1] = sF.P(V(b.min.x, 0, z));
      const lw = val(meas, 'Lens width');
      if (lw) svg += ext(ax1, by + G, ax1, R1) + ext(ax2, by + G, ax2, R1) + dim(ax1, R1, ax2, R1, 'Lens ' + lw, 1);
      const br = val(meas, 'Bridge');
      if (br) svg += ext(ax2, by + G, ax2, R2) + ext(bx1, by + G, bx1, R2) + dim(ax2, R2, bx1, R2, 'Bridge ' + br, 1);
      if (lh) { const [, hy1] = sF.P(V(0, b.max.y, z)), [, hy2] = sF.P(V(0, b.min.y, z));
        svg += ext(rx + G, hy1, xr, hy1) + ext(rx + G, hy2, xr, hy2) + dim(xr, hy1, xr, hy2, 'Lens height ' + lh, 1); }
    } else if (L.length === 1 && lh) {
      const a = L[0], [, hy1] = sF.P(V(0, a.max.y, z)), [, hy2] = sF.P(V(0, a.min.y, z));
      svg += ext(rx + G, hy1, xr, hy1) + ext(rx + G, hy2, xr, hy2) + dim(xr, hy1, xr, hy2, 'Lens height ' + lh, 1);
    }
    out.push(fig('Front', sF, svg));
    /* SIDE */
    if (M.arms.length) {
      const sS = shot(g, 'side', 880), ab = M.arms.map((m) => wbox(m)).reduce((u, b) => u.union(b));
      const [x1, y1] = sS.P(V(ab.max.x, ab.min.y, ab.max.z)), [x2] = sS.P(V(ab.max.x, ab.min.y, ab.min.z)), gb = wbox(g), [, gy] = sS.P(V(ab.max.x, gb.min.y, ab.max.z));
      const al = val(meas, 'Arm length') || (Math.round(ab.max.z - ab.min.z) + ' mm'), base = Math.max(y1, gy), y = base + 34;
      out.push(fig('Side', sS, ext(x1, base + 7, x1, y) + ext(x2, y1 + 7, x2, y) + dim(Math.min(x1, x2), y, Math.max(x1, x2), y, 'Arm length ' + al, 1)));
    }
    S.remove(g);
    const note = meas._note && !/^approximate/i.test(meas._note) ? '<p class="nkm-note">' + esc(meas._note) + '</p>' : '';
    box.innerHTML = '<p class="nkm-h">' + esc(f.label || '') + '</p>' + out.join('') + note;
  }
  function fig(cap, s, svg) {
    return '<figure class="nkm-fig"><div class="nkm-pic" style="aspect-ratio:' + s.w + '/' + s.h + '"><img src="' + s.img + '" alt="' + cap + ' view of your build with measurements"><svg viewBox="0 0 ' + s.w + ' ' + s.h + '" aria-hidden="true">' + ARR + svg + '</svg></div><figcaption>' + cap + ' view</figcaption></figure>';
  }

  /* Comes with: a clean 3/4 picture of the build you have chosen */
  const cbox = root.querySelector('[data-nkz-comes-img]'), cpanel = cbox && cbox.closest('[role="tabpanel"]');
  let csig = '';
  function comesShot(nb) {
    const De = nb.cur().frame.scene.parent, g = De.clone(true); renderer(); S.add(g); g.updateMatrixWorld(true);
    const b = wbox(g), c = b.getCenter(new THREE.Vector3()), sz = b.getSize(new THREE.Vector3()).length();
    const W = 900, H = 600, cam = new THREE.PerspectiveCamera(24, W / H, 1, 5000);
    cam.position.copy(c).add(new THREE.Vector3(150, 62, 265).normalize().multiplyScalar(sz * 2.05)); cam.lookAt(c);
    R.setSize(W, H, false); R.render(S, cam); const url = R.domElement.toDataURL('image/png'); S.remove(g);
    const st = nb.state, f = frames()[st.frame] || {}, eye = !!f.rx_frame || (f.lenses || []).indexOf('demo') > -1;
    const AR = { arc11: 'Arc 11', tpro: 'Trident Pro', tplus: 'Trident Plus', spear: 'Spear' };
    const tt = root.querySelector('[data-nkz-comes-t]'), ts = root.querySelector('[data-nkz-comes-s]');
    if (tt) tt.textContent = eye ? 'Your eyeglasses' : 'Your sunglasses';
    if (ts) ts.textContent = (f.label || '') + (AR[st.arms] ? ' with ' + AR[st.arms] + ' arms' : '');
    cbox.innerHTML = '<img src="' + url + '" alt="' + (eye ? 'Your eyeglasses' : 'Your sunglasses') + '">';
  }
  const ready = (nb) => { if (!nb || !nb.cur) return null; const st = nb.state, cur = nb.cur();
    return (cur && cur.frame && cur.arms && cur.frame.key === 'frame-' + st.frame && cur.arms.key === 'arms-' + st.arms) ? st : null; };
  function tick() {
    if (busy) return; const nb = root._nb, st = ready(nb); if (!st) return; const s2 = JSON.stringify(st);
    if (panel && !panel.hidden && s2 !== sig) { const f = frames()[st.frame]; if (f) { busy = true; try { draw(nb, f); sig = s2; } catch (e) { console.error(e); } busy = false; } }
    if (cbox && cpanel && !cpanel.hidden && s2 !== csig) { busy = true; try { comesShot(nb); csig = s2; } catch (e) { console.error(e); } busy = false; }
  }
  setInterval(tick, 700);
  root.addEventListener('click', (e) => { if (e.target.closest('[data-np-tab]')) setTimeout(tick, 60); });
}
